import { API_BASE_PATH } from "@thyroaid/shared";

/** Validated runtime configuration (WEB-009). Firebase settings are added with sign-in (B01.07). */
export interface FirebaseWebConfig {
  readonly projectId: string;
  readonly apiKey: string;
  readonly authDomain: string;
  readonly appId: string;
  /** Set only in development/test builds; production builds never use emulators. */
  readonly authEmulatorUrl: string | null;
}

export interface AppConfig {
  /** Absolute URL ending in the shared API base path, without trailing slash. */
  readonly apiBaseUrl: string;
  readonly firebase: FirebaseWebConfig;
}

export class ConfigError extends Error {
  constructor(readonly problems: readonly string[]) {
    super(`Invalid ThyroAID configuration:\n- ${problems.join("\n- ")}`);
    this.name = "ConfigError";
  }
}

interface RawEnv {
  readonly VITE_API_BASE_URL?: string | undefined;
  readonly VITE_FIREBASE_PROJECT_ID?: string | undefined;
  readonly VITE_FIREBASE_API_KEY?: string | undefined;
  readonly VITE_FIREBASE_AUTH_DOMAIN?: string | undefined;
  readonly VITE_FIREBASE_APP_ID?: string | undefined;
  readonly VITE_USE_FIREBASE_EMULATORS?: string | undefined;
  readonly VITE_FIREBASE_AUTH_EMULATOR_URL?: string | undefined;
  readonly PROD?: boolean | undefined;
}

/** Values copied unchanged from .env.example. */
const PLACEHOLDER_PREFIX = "replace-with-";

/** Firebase web settings (AUTH-004). Web API keys are public identifiers, not secrets. */
function loadFirebase(env: RawEnv, problems: string[]): FirebaseWebConfig {
  const projectId = env.VITE_FIREBASE_PROJECT_ID?.trim() ?? "";
  if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(projectId)) {
    problems.push("VITE_FIREBASE_PROJECT_ID must be a valid Firebase project ID.");
  }
  const flag = env.VITE_USE_FIREBASE_EMULATORS?.trim() ?? "false";
  if (flag !== "true" && flag !== "false") {
    problems.push('VITE_USE_FIREBASE_EMULATORS must be "true" or "false".');
  }
  const useEmulators = flag === "true";
  let authEmulatorUrl: string | null = null;

  if (useEmulators) {
    if (env.PROD === true) {
      problems.push(
        "Production builds must not use Firebase emulators (VITE_USE_FIREBASE_EMULATORS=true).",
      );
    }
    const raw = env.VITE_FIREBASE_AUTH_EMULATOR_URL?.trim() ?? "";
    try {
      const url = new URL(raw);
      if (url.protocol !== "http:" || url.pathname !== "/" || url.search || url.hash)
        throw new Error();
      authEmulatorUrl = url.origin;
    } catch {
      problems.push(
        "VITE_FIREBASE_AUTH_EMULATOR_URL must be http://host:port when emulators are used.",
      );
    }
  } else if (projectId.startsWith("demo-")) {
    problems.push(
      'A "demo-" Firebase project only works with emulators (set VITE_USE_FIREBASE_EMULATORS=true).',
    );
  }

  const required = (name: keyof RawEnv): string => {
    const value = (env[name] as string | undefined)?.trim() ?? "";
    // Emulators accept any key; real projects need the console values, never the placeholders.
    if (!useEmulators && (value === "" || value.startsWith(PLACEHOLDER_PREFIX))) {
      problems.push(`${name} must be set from the Firebase console (placeholder or empty).`);
    }
    return value || `${projectId}-emulator`;
  };
  return {
    projectId,
    apiKey: required("VITE_FIREBASE_API_KEY"),
    authDomain: required("VITE_FIREBASE_AUTH_DOMAIN"),
    appId: required("VITE_FIREBASE_APP_ID"),
    authEmulatorUrl,
  };
}

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

/**
 * Pure validator: collects every problem instead of stopping at the first, so a misconfigured
 * deployment is fixed in one pass. Throws ConfigError; never returns a half-valid config.
 */
export function loadConfig(env: RawEnv): AppConfig {
  const problems: string[] = [];
  const raw = env.VITE_API_BASE_URL?.trim();
  let apiBaseUrl = "";

  if (!raw) {
    problems.push("VITE_API_BASE_URL is not set (see apps/web/.env.example).");
  } else {
    let url: URL | undefined;
    try {
      url = new URL(raw);
    } catch {
      problems.push(`VITE_API_BASE_URL is not an absolute URL: "${raw}".`);
    }
    if (url) {
      if (url.protocol !== "https:" && url.protocol !== "http:") {
        problems.push(`VITE_API_BASE_URL must use http or https, got "${url.protocol}".`);
      } else if (env.PROD === true && url.protocol === "http:" && !LOCAL_HOSTS.has(url.hostname)) {
        problems.push(
          "VITE_API_BASE_URL must use https in production builds (tokens are sent to it).",
        );
      }
      if (url.search || url.hash || url.username || url.password) {
        problems.push(
          "VITE_API_BASE_URL must not contain credentials, a query string, or a fragment.",
        );
      }
      const path = url.pathname.replace(/\/+$/, "");
      if (path !== API_BASE_PATH && !path.endsWith(API_BASE_PATH)) {
        problems.push(
          `VITE_API_BASE_URL must end with ${API_BASE_PATH} (this build targets API ${API_BASE_PATH}), got "${url.pathname}".`,
        );
      }
      apiBaseUrl = `${url.origin}${path}`;
    }
  }

  const firebase = loadFirebase(env, problems);
  if (problems.length > 0) throw new ConfigError(problems);
  return { apiBaseUrl, firebase };
}
